import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import SaveIcon from "@mui/icons-material/Save";
import TrackChangesOutlinedIcon from "@mui/icons-material/TrackChangesOutlined";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import React, { useMemo, useRef, useState } from "react";

import CorrectiveActionDialog from "./CorrectiveActionDialog";
import CorrectiveActionHistoryDialog from "./CorrectiveActionHistoryDialog";
import { daysBetween, validateCorrectiveAction } from "./correctiveActionUtils";
import StepBar from "./StepBar";

export const ACTION_GRID_TEMPLATE =
  "300px 340px 250px 120px 130px 130px 90px 280px 250px";
export const ACTION_GRID_MIN_WIDTH = 1890;

const actionGridSx = {
  boxSizing: "border-box",
  columnGap: 0.8,
  display: "grid",
  gridTemplateColumns: ACTION_GRID_TEMPLATE,
  minWidth: ACTION_GRID_MIN_WIDTH,
  px: 0.8,
};

const headerCellSx = {
  color: "#fff",
  fontSize: 12,
  fontWeight: 800,
  px: 1,
  py: 1,
};

const valueCellSx = {
  alignItems: "flex-start",
  color: "#10263d",
  display: "flex",
  fontSize: 12,
  minWidth: 0,
  overflowWrap: "anywhere",
  px: 1,
  py: 1.2,
  whiteSpace: "pre-wrap",
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat("fr-FR").format(date);
};

const responsableLabels = (action, users) => {
  const saved = Array.isArray(action.responsables) ? action.responsables : [];
  const ids = Array.isArray(action.responsable_ids)
    ? action.responsable_ids
    : action.responsable_id
      ? [action.responsable_id]
      : [];
  return ids.map((id) => {
    const user = users.find((item) => String(item.id) === String(id));
    const savedUser = saved.find(
      (item) => String(item.responsable_id) === String(id)
    );
    return user?.label || savedUser?.responsable_name || `Utilisateur #${id}`;
  });
};

const ActionStep = ({
  formData,
  navigationEnabled,
  onActionDelete,
  onActionSave,
  onActionValidate,
  onBack,
  onRetryUsers,
  onSave,
  onStepChange,
  saving,
  users,
  usersError,
  usersLoading,
}) => {
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [editor, setEditor] = useState(null);
  const [historyAction, setHistoryAction] = useState(null);
  const [validatingIndex, setValidatingIndex] = useState(null);
  const validationLockRef = useRef(false);
  const [saveError, setSaveError] = useState("");
  const actions = useMemo(
    () =>
      Array.isArray(formData.actions_correctives)
        ? formData.actions_correctives
        : [],
    [formData.actions_correctives]
  );
  const rootCause = useMemo(
    () => String(formData.analyse_cause_racine || "").trim(),
    [formData.analyse_cause_racine]
  );
  const nextOrder = useMemo(
    () =>
      Math.max(0, ...actions.map((action) => Number(action.ordre) || 0)) + 1,
    [actions]
  );

  const handleDialogSave = async (draft) => {
    const saved = await onActionSave(draft, editor?.index ?? null);
    if (saved) {
      setEditor(null);
      setSaveError("");
    }
  };

  const handleValidate = async (action, index) => {
    if (validationLockRef.current || saving || action.status === "confirmed") {
      return;
    }
    validationLockRef.current = true;
    setValidatingIndex(index);
    try {
      await onActionValidate(action, index);
    } finally {
      validationLockRef.current = false;
      setValidatingIndex(null);
    }
  };
  const handleFinalSave = () => {
    if (!actions.length) {
      setSaveError("Ajoutez au moins une action corrective.");
      return;
    }
    const incomplete = actions.some(
      (action) => Object.keys(validateCorrectiveAction(action)).length
    );
    if (incomplete) {
      setSaveError("Une ou plusieurs actions sont incomplètes.");
      return;
    }
    setSaveError("");
    onSave("registre");
  };

  const confirmDelete = async () => {
    const removed = await onActionDelete(deleteIndex);
    if (removed) setDeleteIndex(null);
  };

  return (
    <Box
      aria-label="Étape actions correctives"
      role="region"
      sx={{
        backgroundColor: "#f1f5fb",
        minHeight: "calc(100vh - 56px)",
        p: { xs: 2, sm: 2.5 },
      }}
    >
      <StepBar
        activeStep={4}
        navigationEnabled={navigationEnabled}
        onStepChange={onStepChange}
      />
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #cbd8e6",
          borderRadius: "12px",
          mt: 2.5,
          overflow: "hidden",
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 2.5 }, pb: 1.5 }}>
          <Grid alignItems="center" container spacing={1.5}>
            <Grid item xs>
              <Box sx={{ alignItems: "center", display: "flex", gap: 1.4 }}>
                <Box
                  sx={{
                    alignItems: "center",
                    backgroundColor: "#edf9f2",
                    border: "1px solid #b9dfc8",
                    borderRadius: "50%",
                    color: "#15985b",
                    display: "flex",
                    height: 36,
                    justifyContent: "center",
                    width: 36,
                  }}
                >
                  <LightbulbOutlinedIcon sx={{ fontSize: 19 }} />
                </Box>
                <Box
                  sx={{
                    alignItems: "center",
                    backgroundColor: "#eef3f8",
                    borderRadius: "50%",
                    color: "#36546f",
                    display: "flex",
                    height: 36,
                    justifyContent: "center",
                    width: 36,
                  }}
                >
                  <TrackChangesOutlinedIcon sx={{ fontSize: 19 }} />
                </Box>
                <Box>
                  <Typography color="#10263d" fontSize={15} fontWeight={800}>
                    Étape 5 — Sélection de l’action corrective
                  </Typography>
                  <Typography color="#60758a" fontSize={12}>
                    Choisir l’action qui élimine la cause racine
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm="auto">
              <Box
                sx={{
                  backgroundColor: "#f7fbf8",
                  border: "1px solid #b9dfc8",
                  borderRadius: "8px",
                  color: "#256442",
                  maxWidth: { xs: "100%", sm: 280 },
                  px: 1.5,
                  py: 1,
                }}
              >
                <Typography fontSize={10} fontWeight={800} textTransform="uppercase">
                  Cause racine (Ét. 4)
                </Typography>
                <Typography fontSize={12} noWrap title={rootCause}>
                  {rootCause || "Non renseignée"}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Box
          sx={{
            borderTop: "1px solid #d5e0eb",
            px: { xs: 2, sm: 2.5 },
            py: 1.5,
          }}
        >
          <Typography
            color="#536b91"
            fontSize={11}
            fontWeight={800}
            letterSpacing="0.06em"
            textTransform="uppercase"
          >
            Actions correctives retenues
          </Typography>
          {usersError && (
            <Alert
              action={
                <Button color="inherit" onClick={onRetryUsers} size="small">
                  Réessayer
                </Button>
              }
              severity="error"
              sx={{ mt: 1.2 }}
            >
              {usersError}
            </Alert>
          )}
        </Box>
        <Box sx={{ overflowX: "auto" }}>
          <Box sx={{ minWidth: ACTION_GRID_MIN_WIDTH }}>
            <Box sx={{ ...actionGridSx, backgroundColor: "#1d456b" }}>
              <Box sx={headerCellSx}>Action corrective</Box>
              <Box sx={headerCellSx}>Plan d’action</Box>
              <Box sx={headerCellSx}>Indicateur d’efficacité</Box>
              <Box sx={headerCellSx}>Avancement</Box>
              <Box sx={headerCellSx}>Date début</Box>
              <Box sx={headerCellSx}>Date fin</Box>
              <Box sx={headerCellSx}>Délai</Box>
              <Box sx={headerCellSx}>Responsables</Box>
              <Box sx={headerCellSx}>Actions</Box>
            </Box>
            {actions.length === 0 ? (
              <Box
                sx={{
                  color: "#60758a",
                  minWidth: ACTION_GRID_MIN_WIDTH,
                  py: 4,
                  textAlign: "center",
                }}
              >
                Aucune action corrective. Utilisez « Ajouter une action ».
              </Box>
            ) : (
              actions.map((action, index) => {
                const delay = daysBetween(action.date_debut, action.date_fin);
                const progress = Math.min(
                  100,
                  Math.max(0, Number(action.avancement) || 0)
                );
                const responsables = responsableLabels(action, users);
                return (
                  <Box
                    key={action.plan_action_id || `action-${index}`}
                    sx={{
                      ...actionGridSx,
                      backgroundColor: index % 2 ? "#f8fafc" : "#fff",
                      borderBottom: "1px solid #d5e0eb",
                      minHeight: 82,
                    }}
                  >
                    <Typography component="div" sx={valueCellSx}>
                      {action.action_corrective || "—"}
                    </Typography>
                    <Typography component="div" sx={valueCellSx}>
                      {action.plan_action || "—"}
                    </Typography>
                    <Typography component="div" sx={valueCellSx}>
                      {action.indicateur_efficacite || "—"}
                    </Typography>
                    <Box sx={{ ...valueCellSx, display: "block" }}>
                      <Typography fontSize={12} fontWeight={700}>
                        {progress} %
                      </Typography>
                      <LinearProgress
                        sx={{ borderRadius: 2, height: 6, mt: 0.7 }}
                        value={progress}
                        variant="determinate"
                      />
                    </Box>
                    <Typography component="div" sx={valueCellSx}>
                      {formatDate(action.date_debut)}
                    </Typography>
                    <Typography component="div" sx={valueCellSx}>
                      {formatDate(action.date_fin)}
                    </Typography>
                    <Typography component="div" sx={valueCellSx}>
                      {delay !== "" && delay >= 0 ? `${delay} j` : "—"}
                    </Typography>
                    <Box sx={{ ...valueCellSx, flexWrap: "wrap", gap: 0.5 }}>
                      {responsables.length
                        ? responsables.map((label) => (
                            <Chip key={label} label={label} size="small" />
                          ))
                        : "—"}
                    </Box>
                    <Box
                      sx={{
                        ...valueCellSx,
                        alignItems: "center",
                        gap: 0.4,
                        justifyContent: "center",
                      }}
                    >
                      <Tooltip title="Modifier">
                        <IconButton
                          aria-label={`Modifier l’action ${index + 1}`}
                          disabled={saving}
                          onClick={() => setEditor({ action, index })}
                          size="small"
                          sx={{ color: "#0b8fa3" }}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {action.status === "confirmed" ? (
                        <Chip
                          color="success"
                          icon={<CheckCircleOutlineIcon />}
                          label="Validée"
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Button
                          color="success"
                          disabled={saving || validatingIndex !== null}
                          onClick={() => handleValidate(action, index)}
                          size="small"
                          startIcon={
                            validatingIndex === index ? (
                              <CircularProgress color="inherit" size={14} />
                            ) : (
                              <CheckCircleOutlineIcon />
                            )
                          }
                          variant="contained"
                        >
                          Valider
                        </Button>
                      )}
                      <Tooltip title="Historique">
                        <span>
                          <IconButton
                            aria-label={`Historique de l’action ${index + 1}`}
                            disabled={
                              saving ||
                              validatingIndex !== null ||
                              !action.plan_action_id
                            }
                            onClick={() => setHistoryAction(action)}
                            size="small"
                            sx={{ color: "#536b91" }}
                          >
                            <HistoryOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {action.status !== "confirmed" && (
                        <Tooltip title="Supprimer">
                          <IconButton
                            aria-label={`Supprimer l’action ${index + 1}`}
                            color="error"
                            disabled={saving || validatingIndex !== null}
                            onClick={() => setDeleteIndex(index)}
                            size="small"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5 }}>
          <Button
            disabled={saving}
            onClick={() => setEditor({ action: null, index: null })}
            startIcon={<AddIcon />}
            sx={{
              borderColor: "#c7d3e4",
              color: "#536b91",
              textTransform: "none",
            }}
            variant="outlined"
          >
            Ajouter une action
          </Button>
        </Box>
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 1,
            justifyContent: "flex-end",
            px: { xs: 2, sm: 2.5 },
            py: 2,
          }}
        >
          <Typography color="#d32f2f" fontSize={12} sx={{ mr: "auto" }}>
            {saveError}
          </Typography>
          <Button
            disabled={saving}
            onClick={onBack}
            startIcon={<ArrowBackIcon />}
            sx={{ textTransform: "none" }}
            variant="outlined"
          >
            Retour
          </Button>
          <Button
            disabled={saving}
            onClick={handleFinalSave}
            startIcon={
              saving ? <CircularProgress color="inherit" size={16} /> : <SaveIcon />
            }
            sx={{
              backgroundColor: "#00a6b6",
              textTransform: "none",
              "&:hover": { backgroundColor: "#008f9d" },
            }}
            variant="contained"
          >
            Enregistrer la NC
          </Button>
        </Box>
      </Paper>
      <CorrectiveActionDialog
        action={editor?.action || null}
        nextOrder={nextOrder}
        onClose={() => setEditor(null)}
        onSave={handleDialogSave}
        open={Boolean(editor)}
        saving={saving}
        users={users}
        usersError={usersError}
        usersLoading={usersLoading}
      />
      <CorrectiveActionHistoryDialog
        action={historyAction}
        onClose={() => setHistoryAction(null)}
        open={Boolean(historyAction)}
      />
      <Dialog
        fullWidth
        maxWidth="xs"
        onClose={saving ? undefined : () => setDeleteIndex(null)}
        open={deleteIndex !== null}
      >
        <DialogTitle>Supprimer l’action corrective ?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" variant="body2">
            Cette action sera supprimée définitivement de la liste active.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setDeleteIndex(null)}>
            Annuler
          </Button>
          <Button
            color="error"
            disabled={saving}
            onClick={confirmDelete}
            startIcon={
              saving ? <CircularProgress color="inherit" size={16} /> : null
            }
            variant="contained"
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

ActionStep.propTypes = {
  formData: PropTypes.shape({
    actions_correctives: PropTypes.arrayOf(PropTypes.object),
    analyse_cause_racine: PropTypes.string,
  }).isRequired,
  navigationEnabled: PropTypes.bool,
  onActionDelete: PropTypes.func.isRequired,
  onActionSave: PropTypes.func.isRequired,
  onActionValidate: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onRetryUsers: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onStepChange: PropTypes.func,
  saving: PropTypes.bool.isRequired,
  users: PropTypes.arrayOf(PropTypes.object).isRequired,
  usersError: PropTypes.string,
  usersLoading: PropTypes.bool.isRequired,
};

ActionStep.defaultProps = {
  navigationEnabled: false,
  onStepChange: () => {},
  usersError: null,
};

export default ActionStep;
